import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Alert } from "@mui/material";
import Grid from "@mui/material/Grid";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useState, useEffect } from "react";

import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { copyJsonObject } from "../../util/commonUtils";
import { getServiceVersionsEast, getServiceVersionsWest } from "../../api/system";
import { ERROR_LOADING_LAMBDA_SERVICE_VERSIONS_MESSAGE, TECH_USER_LOCAL_SERVICE_VERSIONS_INFO_MESSAGE } from "../../util/messageConstants";
import PageLoading from "../PageLoading";
import { LAMBDA_SERVICE_VERSIONS_TITLE } from "../../util/constants"

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontWeight: "bold",
    textAlign: "center",
  },
  dialogSubTitle: {
    fontWeight: "bold",
    textAlign: "center",
    fontSize: "18px",
    marginTop: "-25px",
  },
  button: {
    margin: "5px",
    width: "100px",
  },
  rowWithMismatch: {
    backgroundColor: theme.palette.secondary.swaLightRed,
  },
  rowWithoutMismatch: {
    backgroundColor: theme.palette.secondary.white,
  },
  cellWithLocalVersion: {
    backgroundColor: theme.palette.secondary.swaLightBlue,
  },
  headerOuterContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  headerBox: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    flexDirection: "column",
  },
  warningBox: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    flexDirection: "row",
  },
  loadingIcon: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "25px",
  },
  errorIcon: {
    color: theme.palette.primary.swaRed,
  },
  warningMessage: { 
    padding: 15, 
    width: "90%" 
  },
}));

const ServiceVersionDialog = (props) => {
  const classes = useStyles();

  const [serviceVersionList, setServiceVersionFullList] = useState([]);
  const [eastLocalVersionCount, setEastLocalVersionCount] = useState(0);
  const [westLocalVersionCount, setWestLocalVersionCount] = useState(0);
  const [isEastVersionError, setIsEastVersionError] = useState(false);
  const [isWestVersionError, setIsWestVersionError] = useState(false);
  const [isBothRegionError, setIsBothRegionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchServiceList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServiceList = () => {
    Promise.all([getServiceVersionsEast, getServiceVersionsWest]).then(async (serviceVersionResults) => {
      const eastPromise = serviceVersionResults[0];
      const westPromise = serviceVersionResults[1];

      const eastData = await eastPromise();
      if (eastData === null) {
        setIsEastVersionError(true);
      }
      const westData = await westPromise();
      if (westData === null) {
        setIsWestVersionError(true);
      }

      if (!eastData && !westData) {
        setIsBothRegionError(true);
        setIsLoading(false);
      } else {
        const fullServiceList = getFullServiceVersionList(eastData, westData);
        const [eastLocalCount, westLocalCount] = getLocalVersionCounts(fullServiceList);
        setServiceVersionFullList(fullServiceList);
        setEastLocalVersionCount(eastLocalCount);
        setWestLocalVersionCount(westLocalCount);
        setIsLoading(false);
      }
    });
  };

  const getFullServiceVersionList = (eastData, westData) => {
    let fullServiceList = [];
    if (eastData && westData) {
      fullServiceList = getMergedList(eastData, westData);
    } else if (eastData && !westData) {
      fullServiceList = getEastListOnly(eastData);
    } else if (westData && !eastData) {
      fullServiceList = getWestListOnly(westData);
    }
    return fullServiceList;
  };

  const getLocalVersionCounts = (versionList) => {
    let eastCount = versionList.filter((service) => service.eastVersion === "local").length;
    let westCount = versionList.filter((service) => service.westVersion === "local").length;
    return [eastCount, westCount];
  };

  const getEastListOnly = (eastData) => {
    const sortedEastData = getSortedList(eastData);
    let serviceList = sortedEastData.map((service, index) => {
      return {
        id: index + 1,
        module: service.application,
        name: service.name,
        eastVersion: service.eastVersion,
        westVersion: null,
      };
    });
    return serviceList;
  };

  const getWestListOnly = (westData) => {
    const sortedWestData = getSortedList(westData);
    let serviceList = sortedWestData.map((service, index) => {
      return {
        id: index + 1,
        module: service.application,
        name: service.name,
        eastVersion: null,
        westVersion: service.westVersion,
      };
    });
    return serviceList;
  };

  const getMergedList = (eastData, westData) => {
    // Takes both lists and combines the data.
    let mergedServiceVersionList = [];
    let extraWestOnlyLambdas = [];
    let westDataCopy = copyJsonObject(westData);
    const sortedEastData = getSortedList(eastData);
    mergedServiceVersionList = sortedEastData.map((eastService, index) => {
      westDataCopy = westDataCopy.filter((obj) => obj.name !== eastService.name); // Filter westDataCopy down to only versions that are not in the east
      const matchingService = westData.find((westService) => westService.name === eastService.name);
      return {
        id: index + 1,
        module: eastService.application,
        name: eastService.name,
        eastVersion: eastService.eastVersion,
        westVersion: matchingService?.westVersion,
      };
    });
    if (westDataCopy.length > 0) {
      // Add the extra west services to the list
      const sortedWestData = getSortedList(westDataCopy);
      extraWestOnlyLambdas = sortedWestData.map((westService, index) => {
        return {
          id: mergedServiceVersionList.length + index + 1,
          module: westService.application,
          name: westService.name,
          eastVersion: null,
          westVersion: westService.westVersion,
        };
      });
    }
    return mergedServiceVersionList.concat(extraWestOnlyLambdas);
  };

  const getSortedList = (versionList) => {
    let versionListCopy = copyJsonObject(versionList);
    return versionListCopy.sort((a, b) => a.application.localeCompare(b.application));
  };

  const handleOkButtonClicked = () => {
    props.setIsDialogOpen(false);
  };

  const renderErrorMessage = () => {
    return (
      <Grid container justifyContent="center">
        <Box style={{ padding: 20 }}>
          <Alert severity="error">{ERROR_LOADING_LAMBDA_SERVICE_VERSIONS_MESSAGE}</Alert>
        </Box>
      </Grid>
    );
  };

  const renderDialogTitle = () => {
    return (
      <div className={classes.headerOuterContainer}>
        <div className={classes.headerBox}>
          <DialogTitle className={classes.dialogTitle} id="alert-dialog-slide-title">
          {LAMBDA_SERVICE_VERSIONS_TITLE}
          </DialogTitle>
          <Grid container justifyContent="center">
            {isLoading ? (
              <PageLoading />
            ) : isBothRegionError ? (
              renderErrorMessage()
            ) : (
              <DialogTitle className={classes.dialogSubTitle} id="alert-dialog-slide-title">
                (Total: {serviceVersionList?.length || 0})
              </DialogTitle>
            )}
          </Grid>
        </div>
        {isBothRegionError ? (
          <></>
        ) : eastLocalVersionCount || westLocalVersionCount ? (
          <div className={classes.warningBox}>
            <Grid container justifyContent="center">
              <Box className={classes.warningMessage}>
                <Alert severity="warning">
                  {TECH_USER_LOCAL_SERVICE_VERSIONS_INFO_MESSAGE(eastLocalVersionCount, westLocalVersionCount)}
                </Alert>
              </Box>
            </Grid>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };
  const getRowColor = (row) => {
    if (isEastVersionError || isWestVersionError) {
      return classes.rowWithoutMismatch;
    } else {
      if (row.eastVersion !== row.westVersion) {
        return classes.rowWithMismatch;
      } else {
        return classes.rowWithoutMismatch;
      }
    }
  };

  const getCellColorForLocalVersion = (version) => {
    if (version === "local") {
      return classes.cellWithLocalVersion;
    } else {
      return "";
    }
  };

  const renderDialogContent = () => {
    return (
      <DialogContent>
        <Paper elevation={5}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader={true}>
              <TableHead>
                <TableRow>
                  <TableCell>SL No.</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>East Version</TableCell>
                  <TableCell>West Version</TableCell>
                  <TableCell>Function Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceVersionList.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className={getRowColor(row)}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.id}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {row.module}
                    </TableCell>
                    {isEastVersionError ? (
                      <TableCell>
                        <ErrorIcon className={classes.errorIcon} />
                      </TableCell>
                    ) : (
                      <TableCell className={getCellColorForLocalVersion(row.eastVersion)}>
                        {row.eastVersion || "-"}
                      </TableCell>
                    )}
                    {isWestVersionError ? (
                      <TableCell>
                        <ErrorIcon className={classes.errorIcon} />
                      </TableCell>
                    ) : (
                      <TableCell className={getCellColorForLocalVersion(row.westVersion)}>
                        {row.westVersion || "-"}
                      </TableCell>
                    )}
                    <TableCell>{row.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>
    );
  };

  const renderDialogActions = () => {
    return (
      <DialogActions>
        <div>
          <Button className={classes.button} variant="contained" color="primary" onClick={handleOkButtonClicked}>
            Close
          </Button>
        </div>
      </DialogActions>
    );
  };
  return (
    <>
      <Dialog
        className="detail-dialog"
        data-testid="service-version-login-dialog"
        fullWidth
        fullScreen={!props.isDesktopView}
        maxWidth="md"
        open={props.isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={handleOkButtonClicked}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        {renderDialogTitle()}
        {!isLoading && !isBothRegionError && <>{renderDialogContent()}</>}
        {renderDialogActions()}
      </Dialog>
    </>
  );
};

ServiceVersionDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  setCookieSessionActiveFunc: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default ServiceVersionDialog;
